// Basiert auf https://gitlab.pooliestudios.com/koeln-marathon/app/app-dev
import {parseISO, addSeconds} from 'date-fns';
import {
    getWaypointsForRun,
    RUN_RELAY,
    RUN_FULL,
    RUN_HALF,
    Waypoint,
} from './waypoint';
import HumanReadableTime from './HumanReadableTime';
import {ONE_MI_IN_KM} from './unit';

export const SEX_ALL = 'all';
export const SEX_MALE = 'male';
export const SEX_FEMALE = 'female';

export interface RunnerInterface {
    year: number;
    startNumber: string;
    run?: string;
}

export interface RunInterface {
    year: number;
    run: string;
}

export class Runner implements RunnerInterface, RunInterface {
    public year: number;

    public startNumber: string;

    public run: string;

    public lastName?: string;

    public firstName?: string;

    public relayName?: string;

    public runners?: any[];

    public clubName?: string;

    public startTimestamp?: string;

    public results: any[] = [];

    public badges?: string[];

    public profilePictureUrl?: string;

    public certificateUrl?: string;

    public ageGroup?: {min: number, max?: number};

    public rankTotal?: number;

    public rankAge?: number;

    public personalEta?: number;

    public waypoints: Array<Waypoint> = [];

    public eta: HumanReadableTime = new HumanReadableTime(0);

    private cachedHasFinished: boolean = false;

    constructor(initialData?: Partial<Runner>) {
        this.waypoints = getWaypointsForRun(initialData.run);

        this.assign(initialData);
    }

    static getUuid(runner: RunnerInterface): string {
        return `${runner.year}-${runner.startNumber}`;
    }

    get uuid(): string {
        return Runner.getUuid(this);
    }

    assign(newData: Partial<Runner>) {
        Object.assign(this, newData);
        this.update();
    }

    update() {
        this.updateWaypoints();
        this.updateEta();
    }

    updateWaypoints() {
        const newWaypoints = this.waypoints;
        newWaypoints[0] = new Waypoint({
            ...newWaypoints[0],
            passed: this.startTimestamp !== '',
        });

        this.results.forEach((result, index) => {
            const resultWaypoint = newWaypoints[index + 1];
            resultWaypoint.passed = 'time' in result;
            resultWaypoint.time.time = result.time;

            if ('eta' in result) {
                resultWaypoint.eta.time = result.eta;
            } else {
                resultWaypoint.eta.time = 0;
            }

            newWaypoints[index + 1] = new Waypoint(resultWaypoint);
        });

        this.waypoints = newWaypoints;
    }

    updateEta() {
        if (this.hasFinished()) {
            this.eta.time = this.getTimeInSeconds();
        } else if (this.hasResults()) {
            this.eta.time = this.results[this.results.length - 1].eta;
        } else {
            this.eta.time = 0;
        }
    }

    isRunFull(): boolean {
        return this.run === RUN_FULL;
    }

    isRunHalf(): boolean {
        return this.run === RUN_HALF;
    }

    isRunRelay(): boolean {
        return this.run === RUN_RELAY;
    }

    hasDetailedData(): boolean {
        return typeof this.rankTotal !== 'undefined';
    }

    hasStarted(): boolean {
        return this.startTimestamp && parseISO(this.startTimestamp) < new Date();
    }

    hasFinished(): boolean {
        if (this.cachedHasFinished) return true;

        const hasFinished = this.hasResults() && 'time' in this.results[this.results.length - 1];
        if (hasFinished) {
            this.cachedHasFinished = true;
        }

        return hasFinished;
    }

    isRunning(): boolean {
        return this.hasStarted() && !this.hasFinished();
    }

    getTimeInSeconds(): number {
        if (this.hasFinished()) {
            return this.results[this.results.length - 1].time;
        }

        if (this.hasStarted()) {
            const milliseconds = new Date().getTime() - parseISO(this.startTimestamp)
                .getTime();

            if (milliseconds < 0) {
                return 0;
            }

            return Math.round(milliseconds / 1000);
        }

        return -1;
    }

    shouldFinishSoon(): boolean {
        // Wird in 3 Minuten den ETA erreichen
        return this.hasResults() && this.eta.time - this.getTimeInSeconds() < 180;
    }

    isOutOfTime(): boolean {
        if (!this.hasStarted()) return false;

        switch (this.run) {
            case RUN_FULL:
            case RUN_RELAY:
                // 6,5 Stunden
                return this.getTimeInSeconds() > 23400;
            case RUN_HALF:
                // 3,5 Stunden
                return this.getTimeInSeconds() > 12600;
            default:
                return false;
        }
    }

    getTime(): HumanReadableTime {
        return new HumanReadableTime(this.getTimeInSeconds());
    }

    hasResults(): boolean {
        return this.results.length > 0;
    }

    getLastPassedWaypoint(): Waypoint {
        const passedWaypoints = this.waypoints.filter((waypoint) => waypoint.passed);
        return passedWaypoints[passedWaypoints.length - 1];
    }

    getDistanceFinish(): number {
        return this.waypoints[this.waypoints.length - 1].distance;
    }

    getDistanceResults(): number {
        if (this.hasResults()) {
            return this.getLastPassedWaypoint().distance;
        }

        return 0;
    }

    getDistanceInterpolated(): number {
        const lastPassedWaypoint = this.getLastPassedWaypoint();
        const timeSinceLastPassedWaypoint = this.getTimeInSeconds() - lastPassedWaypoint.time.time;
        const distanceSinceLastPassedWaypoint = this.getSpeedBasedOnResultsInKmH() * (
            timeSinceLastPassedWaypoint / 3600
        );
        const distanceInterpolated = this.getDistanceResults() + distanceSinceLastPassedWaypoint;

        if (distanceInterpolated > this.getDistanceFinish()) {
            return this.getDistanceFinish();
        }

        if (distanceInterpolated < 0) {
            return 0;
        }

        return distanceInterpolated;
    }

    getWaypointForDistance(distance): Waypoint {
        return this.waypoints.find((waypoint) => waypoint.distance === distance);
    }

    getTimeForDistance(distance): HumanReadableTime {
        const waypoint = this.getWaypointForDistance(distance);
        if (waypoint && waypoint.passed && waypoint.time.time !== -1) {
            return waypoint.time;
        }

        return new HumanReadableTime(0);
    }

    getEtaForDistance(distance: number): HumanReadableTime {
        const waypoint = this.getWaypointForDistance(distance);
        if (waypoint && waypoint.eta.time !== 0) {
            return waypoint.eta;
        }

        return new HumanReadableTime(0);
    }

    isEtaDueForDistance(distance: number): boolean {
        const eta = this.getEtaForDistance(distance);
        const time = this.getTimeInSeconds();
        if (eta && time) {
            return time >= eta.time;
        }

        return false;
    }

    getSpeedBasedOnResultsInKmH(): number {
        if (!this.hasResults()) return 8; // konservativer Durchschnittswert - 8 km/h

        const timeLastPassedWaypointInHours: number = this.getLastPassedWaypoint().time.time / 3600;
        return this.getDistanceResults() / timeLastPassedWaypointInHours;
    }

    getPaceBasedOnResultsInKm(): HumanReadableTime {
        if (!this.hasResults()) return new HumanReadableTime(0);

        const timeLastPassedWaypointInSeconds = this.getLastPassedWaypoint().time.time;
        return new HumanReadableTime(timeLastPassedWaypointInSeconds / this.getDistanceResults());
    }

    getPaceBasedOnResultsInMi(): HumanReadableTime {
        const pace = this.getPaceBasedOnResultsInKm();
        pace.time *= ONE_MI_IN_KM;
        return pace;
    }

    getEtaTimeOfDay(): Date {
        return addSeconds(parseISO(this.startTimestamp), this.results[this.results.length - 1].eta);
    }

    getFullName(relayPosition?: number): string {
        if (this.run === RUN_RELAY) {
            if (relayPosition !== undefined) {
                return `${this.runners[relayPosition].firstName} ${this.runners[relayPosition].lastName}`;
            }

            return this.relayName;
        }

        return `${this.firstName} ${this.lastName}`;
    }

    getInitials(): string {
        let initials = '';
        if (this.run === RUN_RELAY) {
            const relayNameWords = this.relayName.trim()
                .split(' ');
            if (relayNameWords.length > 1) {
                initials = relayNameWords[0][0] + relayNameWords[relayNameWords.length - 1][0];
            } else {
                initials = this.relayName.slice(0, 2);
            }
        } else {
            const lastNameWords = this.lastName.trim()
                .split(' ');
            initials = this.firstName[0] + lastNameWords[lastNameWords.length - 1][0];
        }

        return initials.toUpperCase();
    }

    hasBadges(): boolean {
        return this.badges && this.badges.length > 0;
    }

    hasRankTotal(): boolean {
        return this.rankTotal && this.rankTotal !== -1;
    }

    hasRankAge(): boolean {
        return this.rankAge && this.rankAge !== -1;
    }

    getAgeGroupString(): string {
        if (!this.ageGroup) return '';

        let ageGroupString = '';
        if (!this.ageGroup.max) {
            ageGroupString += '>';
        }

        ageGroupString += this.ageGroup.min;

        if (this.ageGroup.max) {
            ageGroupString += `-${this.ageGroup.max}`;
        }

        return ageGroupString;
    }

    isFastEnoughForPersonalEta(): boolean {
        return this.eta.time <= this.personalEta;
    }

    getPaceForPersonalEtaInKm(): HumanReadableTime {
        return new HumanReadableTime(this.personalEta / this.getDistanceFinish());
    }

    getPaceForPersonalEtaInMi(): HumanReadableTime {
        const pace = this.getPaceForPersonalEtaInKm();
        pace.time *= ONE_MI_IN_KM;
        return pace;
    }
}
